import * as React from 'react'
import clsx from 'clsx'
import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area'

import { useMergeRefs } from '../../hooks/useMergeRefs'

import styles from './ScrollArea.module.css'

type ScrollAreaElement = React.ElementRef<typeof ScrollAreaPrimitive.Viewport>

type ScrollAreaProps = {
  /**
   * Additional CSS class to apply to the viewport
   */
  className?: string
  /**
   * Size of the scrollbar
   * @default medium
   */
  size?: 'small' | 'medium'
  /**
   * The orientation of the scrollbars
   * @default vertical
   */
  scrollbars?: 'vertical' | 'horizontal' | 'both'
  /**
   * Describes the nature of scrollbar visibility
   * `auto` means that scrollbars are visible when content is overflowing
   * `always` means that scrollbars are always visible regardless of whether the content is overflowing.
   * `scroll` means that scrollbars are visible when the user is scrolling
   * `hover` when the user is scrolling along its corresponding orientation and when the user is hovering over the scroll area
   * `none` means that scrollbars are never visible
   * @default auto
   */
  visibility?: 'auto' | 'always' | 'scroll' | 'hover' | 'none'
  /**
   * Content to render inside the scroll area
   */
  children?: React.ReactNode
  /**
   * Size (thickness) of the fade overlays in px or CSS units
   * @default 40
   */
  gradientFade?: number | string
} & React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Viewport>

const getFadeStyle = (gradientFade: number | string): React.CSSProperties =>
  ({
    '--scrollarea-fade-size':
      typeof gradientFade === 'number' ? `${gradientFade}px` : gradientFade,
  } as React.CSSProperties)

const ScrollArea = React.forwardRef<ScrollAreaElement, ScrollAreaProps>(
  (
    {
      className,
      size = 'medium',
      scrollbars = 'vertical',
      visibility = 'auto',
      gradientFade,
      ...props
    }: ScrollAreaProps,
    forwardedRef,
  ) => {
    const [showTopFade, setShowTopFade] = React.useState(false)
    const [showBottomFade, setShowBottomFade] = React.useState(false)
    const [showLeftFade, setShowLeftFade] = React.useState(false)
    const [showRightFade, setShowRightFade] = React.useState(false)
    const viewportRef = React.useRef<ScrollAreaElement>(null)

    // Merge refs
    const mergedRefs = useMergeRefs(forwardedRef, viewportRef)

    // Update fade visibility on scroll/resize
    const updateFade = React.useCallback(() => {
      const el = viewportRef.current
      if (!el) return
      const {
        scrollTop,
        scrollHeight,
        clientHeight,
        scrollLeft,
        scrollWidth,
        clientWidth,
      } = el
      setShowTopFade(scrollTop > 1)
      setShowBottomFade(scrollTop < scrollHeight - clientHeight - 1)
      setShowLeftFade(scrollLeft > 1)
      setShowRightFade(scrollLeft < scrollWidth - clientWidth - 1)
    }, [])

    React.useEffect(() => {
      const el = viewportRef.current
      if (!el) return
      updateFade()
      el.addEventListener('scroll', updateFade)
      window.addEventListener('resize', updateFade)
      return () => {
        el.removeEventListener('scroll', updateFade)
        window.removeEventListener('resize', updateFade)
      }
    }, [updateFade])

    return (
      <ScrollAreaPrimitive.Root
        type={visibility === 'none' ? 'scroll' : visibility}
        scrollHideDelay={1000}
        className={clsx(styles.root, styles[size], className)}
      >
        {gradientFade ? (
          <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            <ScrollAreaPrimitive.Viewport
              {...props}
              ref={mergedRefs}
              className={styles.viewport}
              style={{ width: '100%', height: '100%' }}
            />
            {/* Fades for vertical */}
            {scrollbars !== 'horizontal' && (
              <div
                className={clsx(
                  styles.fade,
                  styles.fadeVertical,
                  styles.fadeTop,
                )}
                style={{
                  ...getFadeStyle(gradientFade),
                  opacity: showTopFade ? 1 : 0,
                }}
              />
            )}
            {scrollbars !== 'horizontal' && (
              <div
                className={clsx(
                  styles.fade,
                  styles.fadeVertical,
                  styles.fadeBottom,
                )}
                style={{
                  ...getFadeStyle(gradientFade),
                  opacity: showBottomFade ? 1 : 0,
                }}
              />
            )}
            {/* Fades for horizontal */}
            {scrollbars !== 'vertical' && (
              <div
                className={clsx(
                  styles.fade,
                  styles.fadeHorizontal,
                  styles.fadeLeft,
                )}
                style={{
                  ...getFadeStyle(gradientFade),
                  opacity: showLeftFade ? 1 : 0,
                }}
              />
            )}
            {scrollbars !== 'vertical' && (
              <div
                className={clsx(
                  styles.fade,
                  styles.fadeHorizontal,
                  styles.fadeRight,
                )}
                style={{
                  ...getFadeStyle(gradientFade),
                  opacity: showRightFade ? 1 : 0,
                }}
              />
            )}
          </div>
        ) : (
          <ScrollAreaPrimitive.Viewport
            {...props}
            ref={mergedRefs}
            className={styles.viewport}
          />
        )}

        <div className={styles.focusRing} />

        {scrollbars !== 'vertical' ? (
          <ScrollAreaPrimitive.Scrollbar
            data-radius={4}
            orientation="horizontal"
            className={clsx(
              styles.scrollbar,
              visibility === 'none' && styles.hidden,
            )}
          >
            <ScrollAreaPrimitive.Thumb
              className={clsx(
                styles.thumb,
                visibility === 'none' && styles.hidden,
              )}
            />
          </ScrollAreaPrimitive.Scrollbar>
        ) : null}

        {scrollbars !== 'horizontal' ? (
          <ScrollAreaPrimitive.Scrollbar
            data-radius={4}
            orientation="vertical"
            className={clsx(
              styles.scrollbar,
              visibility === 'none' && styles.hidden,
            )}
          >
            <ScrollAreaPrimitive.Thumb
              className={clsx(
                styles.thumb,
                visibility === 'none' && styles.hidden,
              )}
            />
          </ScrollAreaPrimitive.Scrollbar>
        ) : null}

        {scrollbars === 'both' ? (
          <ScrollAreaPrimitive.Corner
            className={clsx(
              styles.corner,
              visibility === 'none' && styles.hidden,
            )}
          />
        ) : null}
      </ScrollAreaPrimitive.Root>
    )
  },
)
ScrollArea.displayName = 'ScrollArea'

export { ScrollArea }
export type { ScrollAreaProps }
